import {
  IOnboardingWizardState,
} from "../../Interfaces/IOnboardingWizardState";

/**
 * Step names, matching the requirement inventory (S1–S7) and the server's own list. Labels only —
 * completion is decided by the shared engine on the server (story 5588), never here.
 */
export const ONBOARDING_STEP_LABELS = [
  "Client Information",
  "Services & Testing",
  "Sites",
  "Contacts",
  "Invoicing",
  "Site Requirements",
  "Review & Submit",
];

export const TOTAL_ONBOARDING_STEPS = ONBOARDING_STEP_LABELS.length;

/** Story 5589: a step number outside 1–7 falls back to step 1. */
export const stepFromParam = (raw: string | null | undefined): number => {
  const parsed = parseInt(`${raw ?? ""}`, 10);
  if (isNaN(parsed) || parsed < 1 || parsed > TOTAL_ONBOARDING_STEPS) return 1;
  return parsed;
};

/**
 * Story 5589: every step already visited stays reachable, so does the one immediately after the
 * furthest reached; anything beyond is locked.
 */
export const isStepReachable = (
  step: number,
  maxReachableStep: number,
): boolean => step <= (maxReachableStep || 1);

/** Locked steps say why rather than being silently inert (story 5589). */
export const STEP_LOCK_REASON = "Complete the earlier steps first.";

export const isStepComplete = (
  state: IOnboardingWizardState | null,
  step: number,
): boolean => Boolean(state?.stepsComplete?.[step - 1]);

/** Steps 1–6; step 7 is the review itself. */
export const areCaptureStepsComplete = (
  state: IOnboardingWizardState | null,
): boolean =>
  Boolean(state) && (state.outstandingSteps?.length ?? 0) === 0;

/**
 * The submit gate (stories 5591/5594/5595): every step complete through the shared rules, the
 * signed-in user being the primary relationship manager, the capture confirmation ticked, and the
 * onboarding not already submitted. The server re-checks all of it.
 */
export const canSubmitOnboarding = (
  state: IOnboardingWizardState | null,
  captureConfirmed: boolean,
): boolean =>
  Boolean(state) &&
  !state.isSubmitted &&
  state.canSubmit &&
  captureConfirmed &&
  areCaptureStepsComplete(state);

/** Why Submit is unavailable, for the button's tooltip. */
export const submitBlockedReason = (
  state: IOnboardingWizardState | null,
  captureConfirmed: boolean,
): string | null => {
  if (!state || state.isSubmitted) return null;
  if (!state.canSubmit)
    return "Only the client's primary relationship manager can submit this onboarding.";
  if (!areCaptureStepsComplete(state))
    return "Complete every step before submitting.";
  if (!captureConfirmed)
    return "Confirm the information was captured with the client.";
  return null;
};

/**
 * What each step needs to read complete — the shared engine's rules in words (story 5588). Shown on
 * the step and used as the reason its own advance action blocks (story 5589).
 */
export const STEP_REQUIREMENTS = [
  "Company name, supporting region, country, the owning Imperans franchise, a client type, an industry, a primary relationship manager, and a company code of 5–10 uppercase letters or digits that is unique in both the CRM and Imperans.",
  "A service line, every enabled testing occasion complete with its collection methods and alcohol band, and no specialist review still pending.",
  "At least one site, each linked to the Imperans clinic that will service it.",
  "At least one contact, one requesting test personnel, and one with results access.",
  "An invoicing arrangement, a valid invoice email, and every Xero key complete with its name, key and timing — plus an allocation once a second key exists.",
  "An answer to all four site questions. No is an answer.",
  "Every earlier step complete, the client confirmation ticked, and you as the client's primary relationship manager.",
];

/**
 * Steps 1, 3 and 4 are captured on the client record itself — the wizard and the record share one
 * stored value per field, so nothing is asked twice (P.6). These are the sections that own them.
 */
export const CAPTURE_ON_RECORD: Record<number, { label: string; path: string }> =
  {
    1: { label: "Open Details", path: "details" },
    3: { label: "Open Offices", path: "company-offices" },
    4: { label: "Open Contacts", path: "company-contacts" },
  };

export type ReviewSection = {
  key: string;
  title: string;
  /** The wizard step that owns the section — step 6 owns two of them (S7.1). */
  ownerStep: number;
  /** Shown instead of the section disappearing when nothing was captured. */
  emptyLine: string;
  count?: number;
};

/**
 * Story 5593: all seven summary sections render in fixed order for every client regardless of
 * service line — the same order the SOP prints in — each with the step that owns it.
 */
export const reviewSections = (
  state: IOnboardingWizardState | null,
): ReviewSection[] => [
  {
    key: "clientInformation",
    title: "Client Information",
    ownerStep: 1,
    emptyLine: "Not provided.",
  },
  {
    key: "servicesTesting",
    title: "Services & Testing",
    ownerStep: 2,
    emptyLine: "Not provided.",
  },
  {
    key: "sites",
    title: "Office/Site Locations",
    ownerStep: 3,
    emptyLine: "No sites added.",
    count: state?.sites?.length ?? 0,
  },
  {
    key: "contacts",
    title: "Contacts & Result Distribution",
    ownerStep: 4,
    emptyLine: "No contacts added.",
    count: state?.contacts?.length ?? 0,
  },
  {
    key: "invoicing",
    title: "Invoicing Information",
    ownerStep: 5,
    emptyLine: "No Xero keys captured.",
  },
  {
    key: "information",
    title: "Information",
    ownerStep: 6,
    emptyLine: "Not provided.",
  },
  {
    key: "siteRequirements",
    title: "Site Requirements & Additional Information",
    ownerStep: 6,
    emptyLine: "Not provided.",
  },
];

/** Story 5590: the HealthTick half of step 2 is offered to New Zealand clients only. */
export const showsHealthTick = (state: IOnboardingWizardState | null): boolean =>
  Boolean(state?.isHealthTickEligible) &&
  Boolean(state?.services?.serviceLine?.match(/healthtick|both/i));

export const showsTdda = (state: IOnboardingWizardState | null): boolean => {
  const line = state?.services?.serviceLine;
  if (!line) return true;
  return /tdda|both/i.test(line);
};

/** A pending specialist review is reported against the step that carries it (story 5594). */
export const isReviewPending = (
  state: IOnboardingWizardState | null,
): boolean => state?.healthTickReview?.status === 1;

export const emDashWhenBlank = (value: any): string => {
  if (value === null || value === undefined || `${value}`.trim() === "")
    return "—";
  return `${value}`;
};

/** Site questions read Yes / No / an em dash while unanswered (No is an answer — S6.1). */
export const siteAnswerLabel = (answer: boolean | null | undefined): string => {
  if (answer === true) return "Yes";
  if (answer === false) return "No";
  return "—";
};
